import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { ButtonType } from '@app/components/button';
import { CardComponent } from '@app/components/card';
import { KeyValueCollectionComponent } from '@app/components/key-value-collection';
import { SpinnerComponent } from '@app/components/spinner';
import { TitleComponent } from '@app/components/title-section';
import { ViewComponent, ViewDirection } from '@app/components/view';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { Spacing } from '@app/shared/types';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import {
  ContractPaymentType,
  EnterpriseTenantContract,
} from '@skyfit/core-data-library';

import moment from 'moment';
import { Observable } from 'rxjs';

interface AccountDataItem {
  key: string;
  value: string;
}
@Component({
  selector: 'app-account',
  standalone: true,
  imports: [
    CardComponent,
    TitleComponent,
    CommonModule,
    RouterModule,
    ViewComponent,
    KeyValueCollectionComponent,
    SpinnerComponent,
    TranslateDirective,
    TranslatePipe,
  ],
  templateUrl: './account.component.html',
})
export class AccountComponent implements OnInit {
  private readonly adminTenantService = inject(AdminTenantService);
  private readonly translatorService = inject(TranslatorService);
  readonly destroyRef = inject(DestroyRef);
  isLoading = signal(true);
  tenant$: Observable<any>;
  private readonly dateFormat = 'MM/DD/YY';
  invoice = false;
  accountData?: AccountDataItem[];
  ButtonType = ButtonType;
  ViewDirection = ViewDirection;
  Spacing = Spacing;

  ngOnInit() {
    this.tenant$ = this.adminTenantService.tenant$;
    this.tenant$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tenant) => {
        if (tenant?.contracts?.length > 0) {
          const contract: EnterpriseTenantContract = tenant.contracts.reduce(
            (
              latestContract: EnterpriseTenantContract,
              currentContract: EnterpriseTenantContract
            ) =>
              currentContract.start > latestContract.start
                ? currentContract
                : latestContract
          );

          const displayContract = { ...contract };

          if (displayContract.paymentType === ContractPaymentType.PEPM) {
            displayContract.paymentAmount *= displayContract.numberOfLicenses;
          }

          this.accountData = [
            {
              key: this.translatorService.translate('account.startDate'),
              value: displayContract.start
                ? moment(displayContract.start).format(this.dateFormat)
                : 'N/A',
            },
            {
              key: this.translatorService.translate('account.renewalDate'),
              value: displayContract.expiration
                ? moment(displayContract.expiration).format(this.dateFormat)
                : 'N/A',
            },
            {
              key: this.translatorService.translate('account.paymentAmount'),
              value: displayContract.paymentAmount
                ? `$${displayContract.paymentAmount.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : 'N/A',
            },
            {
              key: this.translatorService.translate('account.subscriptionType'),
              value: displayContract.paymentType ?? 'N/A',
            },
            {
              key: this.translatorService.translate('account.numberOfLicenses'),
              value: displayContract.numberOfLicenses
                ? String(displayContract.numberOfLicenses)
                : 'N/A',
            },
          ];
        }
        this.isLoading.set(false);
      });
  }
}
